/**
 * `RenewalsQuote` renders the renewals quote selection page.
 *
 * Responsibilities:
 * - Loads available renewal products via `ProductApi.getProducts` when not
 *   already present in journey state.
 * - Translates document tree names using `translateDocumentNames`.
 * - Renders `RenewalsQuoteCard` items and tracks the selected quote, duration,
 *   excess, and optional linked products.
 * - Validates that a quote (and required excess/duration) is selected before
 *   navigating forward.
 * - Handles call-centre vs standard renewals behaviour (default payment method,
 *   benefit visibility, and routing).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import {
  Journey,
  PageAbsolutePath,
  PageRelativePath,
  ProductChannel
} from '../../../../consts/Common';
import RenewalsQuoteCard from './RenewalsQuoteCard';
import {
  LinkedProduct,
  ProductData,
  ProductFilter,
  ProductType,
  SelectedLinkedProduct
} from '../../../common/CommonTypes';
import { PaymentMethod } from '../../../common/Constants';
import { getFormattedDate } from '../../../common/HelperMethods';
import {
  getProductInstalmentTotalPriceByPaymentMethod,
  getRenewalBenefits,
  getNoOfInstallments,
  getSelectedProducts,
  hasPaymentType,
  excessExists,
  getUniqueRenewalDurations,
  productHasExcess
} from '../../../common/ProductMethods';
import { ProductApi } from '../../../../api/ProductApi';
import PageHeader from '../../../common/PageHeader';
import { ButtonBack } from '../../../common/Button/ButtonBack';
import { ButtonContinue } from '../../../common/Button/ButtonContinue';
import { useJourney } from '../../Layout';
import { CommonValidation } from '../../../common/CommonValidation/commonValidation';
import LoadingContainer from '../../../common/LoadingContainer/LoadingContainer';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../../app-state/Hooks';
import { translateDocumentNames } from '../../../../helpers/documentsTranslation';

const RenewalsQuote = (): JSX.Element => {
  const [error, setError] = useState('');
  const [selectedLinkedProducts, setSelectedLinkedProducts] = useState<
    SelectedLinkedProduct[]
  >([]);

  const {
    journey,
    onUpdateProducts,
    onUpdateProductDocuments,
    onPageChange,
    onPolicyStartDateChange
  } = useJourney();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [hasProducts, setHasProducts] = useState(false);
  const { dealerCode, token, products, vehicle, promoCode, policyStartDate } =
    journey;
  const quotesNotFound = !products?.length;

  const selectedProduct = useMemo(() => {
    if (products?.length) {
      const selected = getSelectedProducts(products);
      return selected[0];
    }
  }, [products]);

  const selectedQuoteType = useRef<number>();
  const selectedQuote = selectedProduct?.productId;
  const selectedCoverDuration = selectedProduct?.selectedCoverDuration;
  const selectedExcess = selectedProduct?.selectedExcess;

  const appConfig = useAppSelector((s) => s.appConfig);
  const cms = useAppSelector((s) => s.content.quotePage);

  const navigate = useNavigate();
  const isCallCentreJourney = appConfig.journeyType === Journey.CallCentre;

  const processingMessage = t('renewals-quote.processing-message');
  const noMatchingQuotesTitle = t('renewals-quote.no-matching-quotes-title');
  const noQuotesExistsForPromo = t('renewals-quote.no-quotes-exists-for-promo');

  const buttonCheckoutText = t('renewals-quote.button-checkout-text');
  const mandatoryQuoteError = t('renewals-quote.mandatory-quote-error');
  const mandatoryCoverDurationError = t(
    'renewals-quote.mandatory-cover-duration-error'
  );
  const mandatoryExcessError = t('renewals-quote.mandatory-excess-error');
  const buttonBackText = t('renewals-quote.button-back-text');

  useEffect(() => {
    window.scrollTo(0, 0);

    if (products?.length) {
      const result = getSelectedProducts(products);

      if (result?.length) {
        selectedQuoteType.current = result[0].productTypeId;
      }

      const slp: SelectedLinkedProduct[] = products
        .map((q) => q.selectedLinkedProducts || [])
        .reduce((a, b) => a.concat(b), []);

      setSelectedLinkedProducts(slp);
    } else {
      loadProducts();
    }
  }, []);

  const loadProducts = () => {
    const controller = new AbortController();

    const successCallback = (data: ProductData) => {
      // TODO: ProductData is incorrect type (ProductServer looks more appropriate)
      const retrivedProducts: ProductType[] = data.products.map((product) => ({
        ...product,
        isSelected: false,
        productVariants: product.productVariants.map((variant) => ({
          ...variant,
          isSelected: false
        }))
      }));

      const translatedDocuments = translateDocumentNames(data.documents);

      onUpdateProducts(retrivedProducts);
      onUpdateProductDocuments(translatedDocuments);
      setHasProducts(data.hasProducts);
      setLoading(false);
    };

    const failureCallback = () => {
      console.log('get products failed');
      setHasProducts(false);
      setLoading(false);
    };

    const productFilter: ProductFilter = {
      dealerCode: dealerCode,
      firstRegistrationDate: vehicle.vehicleDetails.registrationDate,
      vehiclePurchaseDate: getFormattedDate(vehicle.vehicleDeliveryDate),
      vehicleValue: vehicle.vehiclePurchasePrice,
      currentMileage: vehicle.currentMileage ?? 0,
      modelTrimId: vehicle.vehicleDetails.modelTrimId,
      fuelTypeId: vehicle.vehicleDetails.fuelTypeCode,
      engineSize: parseInt(vehicle.vehicleDetails.engineSize),
      enginePowerBHP: vehicle.vehicleDetails.enginePowerBHP ?? 0,
      enginePowerKWH: vehicle.vehicleDetails.enginePowerKWH ?? 0,
      driveTypeId: vehicle.vehicleDetails.driveTypeId?.toString() ?? '',
      vrn: vehicle.vehicleDetails.vrn,
      channel: ProductChannel.RenewalJourney,
      promoCode: promoCode,
      countryCode: appConfig.countryCode,
      makeId: vehicle.vehicleDetails.makeId
    };

    setLoading(true);
    new ProductApi().getProducts(
      productFilter,
      token,
      successCallback,
      failureCallback,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const onSelectionChange = ({
    id,
    type,
    duration,
    excess
  }: {
    id: number;
    type: number;
    duration?: number;
    excess?: number;
  }) => {
    selectedQuoteType.current = type;
    updateSelectedProducts({ id, type, duration, excess });
  };

  const onLinkedOptionalQuoteSelectionChange = (
    quoteCardId: number,
    linkedProductTypeId: number
  ) => {
    let updatedLinkedProducts: SelectedLinkedProduct[] = [];

    const exists = selectedLinkedProducts.find(
      (slp) =>
        slp.quoteCardId === quoteCardId &&
        slp.linkedProductTypeId === linkedProductTypeId
    );

    if (exists) {
      //remove it
      updatedLinkedProducts = selectedLinkedProducts.filter(
        (slp) =>
          !(
            slp.quoteCardId === quoteCardId &&
            slp.linkedProductTypeId === linkedProductTypeId
          )
      );
    } else {
      //add it
      updatedLinkedProducts = [
        ...selectedLinkedProducts,
        { quoteCardId, linkedProductTypeId }
      ];
    }

    setSelectedLinkedProducts(updatedLinkedProducts);
  };

  const updateSelectedProducts = ({
    id,
    type,
    duration,
    excess
  }: {
    id: number;
    type: number;
    duration?: number;
    excess?: number;
  }) => {
    // TODO: refactor. lots of duplicated lines and not optimized render performance
    selectedQuoteType.current = type;
    const updatedProducts = products.map((q) =>
      q.productId === id
        ? {
            ...q,
            isSelected: true,
            policyStartDate: undefined,
            selectedTyreTypeId: undefined,
            selectedClaimLimit: undefined,
            selectedCoverLevelId: selectedQuote,
            selectedPaymentMethod: undefined,
            selectedLinkedProducts: selectedLinkedProducts,
            productVariants: q.productVariants.map((variant) =>
              variant.isSelected === true
                ? { ...variant, isSelected: false }
                : variant
            ),
            selectedCoverDuration: duration ?? 0,
            selectedExcess: excess
          }
        : {
            ...q,
            isSelected: false,
            policyStartDate: undefined,
            selectedTyreTypeId: undefined,
            selectedClaimLimit: undefined,
            selectedCoverLevelId: undefined,
            selectedPaymentMethod: undefined,
            selectedLinkedProductTypes: undefined,
            selectedLinkedProducts: undefined,
            productVariants: q.productVariants.map((variant) =>
              variant.isSelected === true
                ? { ...variant, isSelected: false }
                : variant
            ),
            selectedCoverDuration: 0,
            selectedExcess: undefined
          }
    );
    onUpdateProducts(updatedProducts);

    onPolicyStartDateChange(
      updatedProducts.filter((p) => p.isSelected)?.[0]?.policyStartDate ??
        policyStartDate
    );
  };

  const onBack = () => {
    goBack();
    onPageChange(PageAbsolutePath.Renewals.Quote, false);
  };

  const goBack = () => {
    navigate(PageRelativePath.VehicleDetails);
  };

  const onCheckout = () => {
    if (validate() === true) {
      if (isCallCentreJourney) navigate(PageRelativePath.PersonalDetails);
      else navigate(PageRelativePath.Summary);
      onPageChange(PageAbsolutePath.Renewals.Quote, true);
    }
  };

  const validate = (): boolean => {
    let isValid = true;

    if (
      CommonValidation.isEmpty(selectedQuote) ||
      (selectedQuote === 0 && selectedQuoteType.current === 0)
    ) {
      isValid = false;
      setError(mandatoryQuoteError);
    } else {
      const selectedProduct = products.find(
        (p) => p.productId === selectedQuote
      );

      if (selectedProduct?.productVariants?.length) {
        const uniqueDurations = getUniqueRenewalDurations(
          selectedProduct.productVariants,
          { excess: selectedExcess }
        );

        if (uniqueDurations.size > 1 && !selectedCoverDuration) {
          isValid = false;
          setError(mandatoryCoverDurationError);
        }

        const uniqueExcesses = new Set(
          selectedProduct.productVariants.map((variant) => variant.excess)
        );
        if (uniqueExcesses.size > 1 && !excessExists(selectedExcess)) {
          isValid = false;
          setError(mandatoryExcessError);
        }
      }
    }
    return isValid;
  };

  const getLinkedProducts = (quote: ProductType) => {
    let linkedProducts: LinkedProduct[] = [];

    const uniqueDurations = getUniqueRenewalDurations(quote.productVariants);

    if (uniqueDurations.size > 1 && quote.selectedCoverDuration) {
      linkedProducts = quote.productVariants
        .filter(
          (variant) => Number(variant.duration) === quote.selectedCoverDuration
        )
        .map((variant) => variant.linkedProducts)
        .reduce((a, b) => a.concat(b), [] as LinkedProduct[]);
    } else {
      linkedProducts = quote.productVariants
        .map((variant) => variant.linkedProducts)
        .reduce((a, b) => a.concat(b), [] as LinkedProduct[]);
    }

    return linkedProducts;
  };

  const getDefaultPaymentMethod = (quote: ProductType) => {
    let result: string = isCallCentreJourney
      ? PaymentMethod.Single
      : PaymentMethod.Monthly;

    if (hasPaymentType([quote], PaymentMethod.Subscription)) {
      result = PaymentMethod.Subscription;
    }

    return result;
  };

  const renderProducts = () => {
    return products.map((q) => {
      const noOfInstallments = getNoOfInstallments([q]);

      const monthlyPrice = getProductInstalmentTotalPriceByPaymentMethod(
        q,
        PaymentMethod.Monthly,
        noOfInstallments,
        selectedLinkedProducts,
        q.selectedCoverDuration,
        q.selectedExcess
      );

      const singlePrice = getProductInstalmentTotalPriceByPaymentMethod(
        q,
        PaymentMethod.Single,
        noOfInstallments,
        selectedLinkedProducts,
        q.selectedCoverDuration,
        q.selectedExcess
      );

      const subscriptionPrice = getProductInstalmentTotalPriceByPaymentMethod(
        q,
        PaymentMethod.Subscription,
        noOfInstallments,
        selectedLinkedProducts,
        0,
        q.selectedExcess
      );

      const keyBenefits = isCallCentreJourney
        ? []
        : getRenewalBenefits(
            cms.productTypeCustomisations,
            q,
            selectedLinkedProducts
          );

      const showAllPricingOptions =
        isCallCentreJourney &&
        !productHasExcess(q) &&
        getUniqueRenewalDurations(q.productVariants).size <= 1;

      return (
        <RenewalsQuoteCard
          key={`quote-${q.productTypeId}-${q.renewalGroupId}`}
          id={q.renewalGroupId}
          productId={q.productId}
          keyBenefitLists={keyBenefits}
          quoteTitle={q.productVariants[0].customerFriendlyName}
          priceMonthly={
            monthlyPrice.firstInstallmentPrice === 0 ? undefined : monthlyPrice
          }
          priceSingle={
            singlePrice.firstInstallmentPrice === 0
              ? undefined
              : singlePrice.firstInstallmentPrice
          }
          priceSubscription={
            subscriptionPrice.firstInstallmentPrice === 0
              ? undefined
              : subscriptionPrice.firstInstallmentPrice
          }
          quoteType={q.productTypeId}
          isPaymentDone={false}
          showMoreInfo={!isCallCentreJourney}
          showSelectOption={true}
          selectedQuote={selectedQuote}
          linkedQuotes={getLinkedProducts(q)}
          hideBenefits={isCallCentreJourney}
          defaultPaymentMethod={getDefaultPaymentMethod(q)}
          selectedOptionalLinkedQuotes={selectedLinkedProducts}
          noOfInstallments={noOfInstallments}
          productVariants={q.productVariants}
          showAllPricingOptions={showAllPricingOptions}
          isVariablePrice={q.isVariablePrice}
          onSelectionChange={onSelectionChange}
          onLinkedOptionalQuoteSelectionChange={
            onLinkedOptionalQuoteSelectionChange
          }
          culture={appConfig.cultureCode}
          selectedCoverDuration={q.selectedCoverDuration ?? 0}
          selectedExcess={q.selectedExcess}
        />
      );
    });
  };

  return (
    <>
      <Helmet>
        <title>{t('page-title.quotations')}</title>
      </Helmet>

      <LoadingContainer loading={loading} message={processingMessage}>
        <div>
          {quotesNotFound === false && (
            <>
              <PageHeader
                title={cms.title}
                description={cms.description}
                descriptionClass='p2'
                information={cms.information}
                warning={cms.warning}
              />
              <p className='p2 bold m1-bottom m2-top'>{cms.selectionText}</p>
            </>
          )}
          {quotesNotFound && isCallCentreJourney && (
            <>
              <h3>{noMatchingQuotesTitle}</h3>
              {hasProducts && (
                <p className='m1-bottom'>{noQuotesExistsForPromo}</p>
              )}
            </>
          )}
          {quotesNotFound && !isCallCentreJourney && (
            <div
              dangerouslySetInnerHTML={{ __html: cms.noQuotesErrorMessage }}
            />
          )}
          <div>{renderProducts()}</div>
          <div className='warning-text m05-left m05-bottom'>{error}</div>
          <div className='flex-row form-nav'>
            <ButtonBack name='btnBack' text={buttonBackText} onClick={onBack} />
            {quotesNotFound === false && (
              <ButtonContinue
                name='btn-checkout'
                text={buttonCheckoutText}
                onClick={onCheckout}
              />
            )}
          </div>
        </div>
      </LoadingContainer>
    </>
  );
};

export default RenewalsQuote;
